import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';

interface SparklineProps {
  label: string;
  values: number[];
  color?: string;
  /** true means a rising value is bad (e.g. failing runs, disconnected agents) */
  higherIsWorse?: boolean;
}

export function Sparkline({ label, values, color = '#26a698', higherIsWorse = false }: SparklineProps) {
  if (values.length < 2) {
    return (
      <div>
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-2 text-sm text-slate-300 dark:text-slate-600">Not enough history yet</p>
      </div>
    );
  }

  const width = 160;
  const height = 40;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');

  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const isFlat = delta === 0;
  const isRising = delta > 0;
  // Simple, readable trend coloring: rising is "worse" only when higherIsWorse is true.
  const trendIsBad = !isFlat && (higherIsWorse ? isRising : !isRising);
  const TrendIcon = isFlat ? Minus : isRising ? TrendingUp : TrendingDown;

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <span
          className={cn(
            'flex items-center gap-0.5 text-xs font-semibold',
            isFlat ? 'text-slate-400' : trendIsBad ? 'text-rose-500' : 'text-pulse-600 dark:text-pulse-400',
          )}
        >
          <TrendIcon size={12} />
          {delta > 0 ? '+' : ''}
          {delta}
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-1 w-full" preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">{last}</p>
    </div>
  );
}
